import * as React from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { DateRange } from 'react-day-picker';
import { formatDateForDisplay, DateFormat } from '@/utils/numberFormat';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

// Import the styles for react-day-picker
import 'react-day-picker/dist/style.css';
import './date-range-picker.css';

interface DatePickerWithRangeProps {
  className?: string;
  date: DateRange | undefined;
  setDate: (date: DateRange) => void;
}

export function DatePickerWithRange({ className, date, setDate }: DatePickerWithRangeProps) {
  const [open, setOpen] = React.useState(false);
  const [fromDate, setFromDate] = React.useState<string>('');
  const [toDate, setToDate] = React.useState<string>('');
  const containerRef = React.useRef<HTMLDivElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);

  // Get organization settings for date formatting and theme
  const { orgSettings } = useOrgSettings();
  const { actualTheme } = useTheme();
  const { t } = useTranslation();
  const dateFormat = (orgSettings?.date_format || 'MDY') as DateFormat;

  // Sync internal state with external date prop
  React.useEffect(() => {
    if (date?.from) {
      setFromDate(format(date.from, 'yyyy-MM-dd'));
    } else {
      setFromDate('');
    }

    if (date?.to) {
      setToDate(format(date.to, 'yyyy-MM-dd'));
    } else {
      setToDate('');
    }
  }, [date]);

  // Handle clicks outside the component
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [open]);

  // Format the date for display in the button
  const formatDisplayDate = () => {
    if (date?.from && date?.to) {
      return `${formatDateForDisplay(date.from, dateFormat)} - ${formatDateForDisplay(date.to, dateFormat)}`;
    } else if (date?.from) {
      return `${formatDateForDisplay(date.from, dateFormat)} - ${t('datePicker.selectEndDate', 'Select end date')}`;
    }
    return t('datePicker.pickDateRange', 'Pick a date range');
  };

  // Handle from date change
  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFromDate(value);

    if (value) {
      const newFromDate = new Date(value);
      const currentToDate = toDate ? new Date(toDate) : undefined;

      // If to date is before from date, clear it
      if (currentToDate && currentToDate < newFromDate) {
        setToDate('');
        setDate({ from: newFromDate, to: undefined });
      } else {
        setDate({ from: newFromDate, to: currentToDate });
      }
    } else {
      setDate({ from: undefined, to: toDate ? new Date(toDate) : undefined });
    }
  };

  // Handle to date change
  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setToDate(value);

    if (value) {
      const newToDate = new Date(value);
      const currentFromDate = fromDate ? new Date(fromDate) : undefined;

      // If from date is after to date, clear from date
      if (currentFromDate && currentFromDate > newToDate) {
        setFromDate('');
        setDate({ from: undefined, to: newToDate });
      } else {
        setDate({ from: currentFromDate, to: newToDate });

        // Auto-close when both dates are selected
        if (currentFromDate) {
          setTimeout(() => setOpen(false), 100);
        }
      }
    } else {
      setDate({
        from: fromDate ? new Date(fromDate) : undefined,
        to: undefined,
      });
    }
  };

  // Handle clear
  const handleClear = () => {
    setFromDate('');
    setToDate('');
    setDate({ from: undefined, to: undefined });
    setOpen(false);
  };

  // Calculate popover position
  const [popoverStyle, setPopoverStyle] = React.useState<React.CSSProperties>({});

  React.useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const popoverHeight = 200; // Approximate height

      let top = rect.bottom + 8;

      // If popover would go below viewport, show it above
      if (rect.bottom + popoverHeight > viewportHeight) {
        top = rect.top - popoverHeight - 8;
        if (top < 0) {
          top = rect.bottom + 8;
        }
      }

      setPopoverStyle({
        position: 'fixed',
        top: `${top}px`,
        left: `${rect.left}px`,
        zIndex: 9999,
        minWidth: `${rect.width}px`,
      });
    }
  }, [open]);

  return (
    <>
      <div className={cn('relative', className)} ref={containerRef}>
        <Button
          variant="outline"
          className={cn(
            'justify-start text-left font-normal w-full',
            !date?.from && 'text-muted-foreground',
            actualTheme === 'dark' &&
              'border-gray-600 bg-gray-800 text-gray-200 hover:bg-gray-700 hover:text-gray-100'
          )}
          onClick={() => setOpen(!open)}
        >
          <CalendarIcon className="mr-2 size-4" />
          {formatDisplayDate()}
        </Button>
      </div>

      {open && (
        <div
          ref={popoverRef}
          className="w-auto rounded-md border bg-white p-4 shadow-lg dark:bg-slate-950"
          style={popoverStyle}
        >
          <div className="space-y-4">
            <div className="mb-3 text-sm font-medium text-slate-700 dark:text-slate-300">
              {t('datePicker.selectDateRange', 'Select Date Range')}
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                  {t('datePicker.fromDate', 'From Date')}
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={handleFromDateChange}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                  max={toDate || undefined}
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                  {t('datePicker.toDate', 'To Date')}
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={handleToDateChange}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                  min={fromDate || undefined}
                />
              </div>
            </div>

            <div className="flex items-center justify-between border-t pt-3">
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {fromDate && toDate
                  ? t('datePicker.rangeSelected', 'Range selected')
                  : fromDate
                    ? t('datePicker.selectEndDate', 'Select end date')
                    : t('datePicker.selectStartDate', 'Select start date')}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClear}
                className={
                  actualTheme === 'dark'
                    ? 'border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700 hover:text-slate-100'
                    : ''
                }
              >
                {t('datePicker.clear', 'Clear')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
